import { Stack, Typography, useTheme } from "@mui/material";
import type { Control } from "react-hook-form";
import { ClientPortfolioDomainEnum } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import type {
	CryptocurrencyFormInput,
	CryptocurrencyFormOutput,
} from "web_myplann/Portfolio/Forms/CryptocurrencyForm/cryptocurrencyFormSchema";
import { useProductCategoryOptions } from "web_myplann/Portfolio/Helpers/useProductCategoryOptions";
import { FlatRowFormDatePicker } from "web_myplann/Shared/Components/FlatRow/FlatRowFormDatePicker";
import { FlatRowFormInput } from "web_myplann/Shared/Components/FlatRow/FlatRowFormInput";
import { FlatRowFormSelect } from "web_myplann/Shared/Components/FlatRow/FlatRowFormSelect";
import { FlatRowFormTextarea } from "web_myplann/Shared/Components/FlatRow/FlatRowFormTextarea";
import { DateFormat } from "web_myplann/Shared/Utils/dateFormats";

type CryptocurrencyFormFieldsProps = {
	control: Control<CryptocurrencyFormInput, unknown, CryptocurrencyFormOutput>;
};

export const CryptocurrencyFormFields = ({
	control,
}: CryptocurrencyFormFieldsProps) => {
	const theme = useTheme();
	const { t } = useResource();

	const productCategoryOptions = useProductCategoryOptions(
		ClientPortfolioDomainEnum.Cryptocurrencies,
	);

	return (
		<>
			<Typography variant="portfolioSectionLabel">
				{t(Resources.Portfolio.AddCryptocurrency.Sections.basicInfo)}
			</Typography>
			<Stack bgcolor={theme.palette.background.paper}>
				<FlatRowFormInput
					control={control}
					name="name"
					label={t(Resources.Portfolio.AddCryptocurrency.Fields.name)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.fill)}
				/>
				<FlatRowFormInput
					control={control}
					name="contractNumber"
					label={t(Resources.Portfolio.AddCryptocurrency.Fields.contractNumber)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.optional)}
				/>
				<FlatRowFormInput
					control={control}
					name="producer"
					label={t(Resources.Portfolio.AddCryptocurrency.Fields.producer)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.optional)}
				/>
				<FlatRowFormInput
					control={control}
					name="product"
					label={t(Resources.Portfolio.AddCryptocurrency.Fields.product)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.optional)}
				/>
				<FlatRowFormSelect
					control={control}
					name="productCategoryId"
					label={t(
						Resources.Portfolio.AddCryptocurrency.Fields.productCategory,
					)}
					options={productCategoryOptions}
					isLast
				/>
			</Stack>

			<Typography variant="portfolioSectionLabel">
				{t(Resources.Portfolio.AddCryptocurrency.Sections.additionalInfo)}
			</Typography>
			<Stack bgcolor={theme.palette.background.paper}>
				<FlatRowFormDatePicker
					control={control}
					name="signedAt"
					label={t(Resources.Portfolio.AddCryptocurrency.Fields.signedAt)}
					views={["year", "month", "day"]}
					displayFormat={DateFormat.Display}
				/>
				<FlatRowFormDatePicker
					control={control}
					name="startAt"
					label={t(Resources.Portfolio.AddCryptocurrency.Fields.startAt)}
					views={["year", "month", "day"]}
					displayFormat={DateFormat.Display}
				/>
				<FlatRowFormDatePicker
					control={control}
					name="endAt"
					label={t(Resources.Portfolio.AddCryptocurrency.Fields.endAt)}
					views={["year", "month", "day"]}
					displayFormat={DateFormat.Display}
					isLast
				/>
			</Stack>

			<Typography variant="portfolioSectionLabel">
				{t(Resources.Portfolio.AddCryptocurrency.Sections.note)}
			</Typography>
			<Stack bgcolor={theme.palette.background.paper}>
				<FlatRowFormTextarea
					control={control}
					name="note"
					label={t(Resources.Portfolio.AddCryptocurrency.Fields.note)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.optional)}
					rows={1}
					isLast
				/>
			</Stack>
		</>
	);
};
